'use client';

import { getTagsFileUrl, getTagsDomain } from 'utils/tags';
import { useRef, useEffect, useState } from 'react';
import { onConsentChange, getActiveGroups, ONETRUST_CATEGORIES, getOptanonCategoryClass } from 'libs/oneTrust';

export default function TagsIframe() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Guard postMessages against the iframe's pre-load contentWindow, which
  // still carries the parent origin — posting before load fails the targeted
  // origin check and logs a noisy console warning. The init payload carries
  // the consent snapshot on load, so dropping pre-load consent replays loses
  // nothing.
  const isIframeLoadedRef = useRef(false);
  const tagsDomain = getTagsDomain();
  const [tagsUrl, setTagsUrl] = useState('');

  useEffect(() => {
    // Ensure the iframe reloads when the page is reloaded
    // oxlint-disable-next-line react/set-state-in-effect -- needs Date.now() cache buster, only runs on mount
    setTagsUrl(`${getTagsFileUrl('chime-com-gtm')}?_=${Date.now()}`);
  }, []);

  // Forward consent changes into the iframed GTM container so tags with
  // non-page-load triggers can react to mid-session opt-out. The iframe
  // element itself is gated by OneTrust's data-src/optanon-category mechanism
  // below, so this only matters once the iframe has already loaded.
  useEffect(() => {
    return onConsentChange(activeGroups => {
      if (!isIframeLoadedRef.current) return;
      iframeRef.current?.contentWindow?.postMessage(
        { message: 'consent', properties: { onetrustActiveGroups: activeGroups.join(',') } },
        tagsDomain,
      );
    });
  }, [tagsDomain]);

  const initGtm = () => {
    // Iframes without src fire a load event for their initial about:blank
    // document. Skip it — at this point the contentWindow still carries the
    // parent origin, so neither the init post nor subsequent consent posts
    // can reach the tags origin. Wait for OneTrust's data-src → src swap.
    const iframe = iframeRef.current;
    if (!iframe?.src.startsWith(tagsDomain)) return;
    isIframeLoadedRef.current = true;
    const message = 'init';
    const properties = {
      windowState: window.history.state,
      path: window.location.pathname,
      params: window.location.search,
      onetrustActiveGroups: getActiveGroups().join(','),
    };

    iframe.contentWindow?.postMessage({ message, properties }, tagsDomain);
  };

  if (!tagsUrl) return null;

  return (
    <iframe
      ref={iframeRef}
      title="Google Tag Manager (tags.chime.com)"
      id="tags_gtm"
      // OneTrust copies data-src → src once Targeting (C0004) consent is
      // granted; without consent no network request fires.
      // https://my.onetrust.com/s/article/UUID-518074a1-a6da-81c3-be52-bae7685d9c94
      className={getOptanonCategoryClass(ONETRUST_CATEGORIES.TARGETING)}
      data-src={tagsUrl}
      sandbox="allow-same-origin allow-scripts"
      onLoad={initGtm}
      style={{
        width: 0,
        height: 0,
        border: 0,
        visibility: 'hidden',
        position: 'absolute',
      }}
    />
  );
}
